import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormGroup } from './FormGroup';
import { Input } from '@vimily/bonjoro-ui-components-v2/Input';
import {
  DropdownSelector,
  SelectorType,
} from '@vimily/bonjoro-ui-components-v2/DropdownSelector';
import { CreditCardInput } from './CreditCardInput';
import { useCountries } from '../../Hooks/useCountries';
import { DropdownAlignment } from '@vimily/bonjoro-ui-components-v2/Dropdown';
import { BillingDetails, CreditCardValue } from '~/types/stripe';
import { ScaMandatePanel } from '../Common/ScaMandatePanel';
import { UpdateCreditCardArgs } from '../../Hooks/useCreditCard';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElement, StripeElements } from '@stripe/stripe-js';
import { ActionPanel } from '../Common/ActionPanel';

export interface CreditCardFormProps {
  ctaLabel?: React.ReactNode;
  ctaClassName?: string;
  cancelLabel?: React.ReactNode;
  hideScaMandate?: boolean;
  isSubmitting?: boolean;
  onSubmit?: (formData: UpdateCreditCardArgs) => void;
  onCancel?: () => void;
}

export function CreditCardForm({
  ctaLabel = 'Confirm payment',
  ctaClassName,
  cancelLabel = 'Cancel',
  hideScaMandate = false,
  isSubmitting,
  onSubmit,
  onCancel,
}: CreditCardFormProps) {
  const elements = useElements();
  const stripe = useStripe();
  const { data: countries } = useCountries({});
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [creditCardDetails, setCreditCardDetails] = useState<CreditCardValue>();
  const [country, setCountry] = useState<string>('AU');
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [postcode, setPostcode] = useState<string>('');

  const countryList = useMemo(
    () =>
      countries?.reduce<Record<string, string>>((items, country) => {
        return {
          ...items,
          [country.value]: country.label,
        };
      }, {}),
    [countries]
  );

  const clearForm = () => {
    setCreditCardDetails(undefined);
    setCountry('');
    setCardHolderName('');
    setAddress('');
    setCity('');
    setState('');
    setPostcode('');
  };

  // Clear form on clean up
  useEffect(() => {
    return () => {
      clearForm();
    };
  }, []);

  const checkFormValidity = useCallback(() => {
    const errors: string[] = [];
    if (creditCardDetails == undefined || !creditCardDetails?.complete) {
      errors.push('credit-card');
    }

    if (cardHolderName == '') {
      errors.push('card-holder-name');
    }

    if (country == '') {
      errors.push('country');
    }

    if (address == '') {
      errors.push('address');
    }

    if (city == '') {
      errors.push('city');
    }

    if (state == '') {
      errors.push('state');
    }

    if (postcode == '') {
      errors.push('postcode');
    }

    setErrorFields(errors);
    return errors.length === 0;
  }, [
    creditCardDetails,
    country,
    cardHolderName,
    address,
    city,
    state,
    postcode,
  ]);

  const onSubmitClick = useCallback(() => {
    if (!checkFormValidity()) return false;

    const cardElement = (elements as StripeElements).getElement(
      CardElement
    ) as StripeCardElement;

    const billingDetails: BillingDetails = {
      name: cardHolderName,
      address: {
        city,
        country,
        state,
        postal_code: postcode,
        line1: address,
      },
    };

    onSubmit?.({
      cardElement,
      billingDetails,
      stripe,
    });
  }, [
    checkFormValidity,
    creditCardDetails,
    country,
    cardHolderName,
    address,
    city,
    state,
    postcode,
    stripe,
  ]);

  return (
    <div className='flex flex-col gap-y-4 min-[534px] w-full'>
      <FormGroup
        label='Card details'
        inputComponent={<CreditCardInput onChange={setCreditCardDetails} />}
        errorLabel={
          errorFields.includes('credit-card')
            ? 'Please enter a valid credit card'
            : undefined
        }
      />
      <FormGroup
        label='Cardholder name'
        inputComponent={
          <Input
            placeholder='John Doe'
            value={cardHolderName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setCardHolderName(e.target.value)
            }
          />
        }
        errorLabel={
          errorFields.includes('card-holder-name')
            ? 'Cardholder name is required'
            : undefined
        }
      />
      <div className='flex flex-row gap-x-4 w-full'>
        <FormGroup
          label='Address'
          inputComponent={
            <Input
              placeholder='42 Wallaby St'
              value={address}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAddress(e.target.value)
              }
            />
          }
          errorLabel={
            errorFields.includes('address') ? 'Address is required' : undefined
          }
        />
        <FormGroup
          label='City'
          inputComponent={
            <Input
              placeholder='Sydney'
              value={city}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCity(e.target.value)
              }
            />
          }
          errorLabel={
            errorFields.includes('city') ? 'City is required' : undefined
          }
        />
      </div>
      <div className='flex flex-row gap-x-4 w-full'>
        <FormGroup
          label='Country'
          inputComponent={
            <DropdownSelector
              type={SelectorType.Select}
              items={countryList}
              className='!w-full'
              dropdownProps={{
                containerAlignment: DropdownAlignment.Start,
                containerClassName: '!w-full',
                id: 'country-selector-dropdown',
                width: 0,
              }}
              bodyLabelClassName='!text-base'
              bodyContainerClassName='!w-full border v2-border-neutral-300 border-solid'
              bodyPlaceholder={
                <span className='text-base v2-text-neutral-300'>Australia</span>
              }
              hideTooltip={true}
              selected={country ? [country] : undefined}
              onSelect={(value: string[]) => setCountry(value[0])}
            />
          }
          errorLabel={
            errorFields.includes('country') ? 'Country is required' : undefined
          }
        />
        <div className='flex flex-row gap-x-4 w-full'>
          <FormGroup
            label='State'
            inputComponent={
              <Input
                placeholder='NSW'
                value={state}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setState(e.target.value)
                }
              />
            }
            errorLabel={
              errorFields.includes('state') ? 'State is required' : undefined
            }
          />
          <FormGroup
            label='Postcode'
            inputComponent={
              <Input
                placeholder='2000'
                value={postcode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPostcode(e.target.value)
                }
              />
            }
            errorLabel={
              errorFields.includes('postcode')
                ? 'Postcode is required'
                : undefined
            }
          />
        </div>
      </div>
      {!hideScaMandate && <ScaMandatePanel />}
      <ActionPanel
        ctaClassName={ctaClassName}
        onCtaClick={onSubmitClick}
        onCancelClick={onCancel}
        ctaLabel={ctaLabel}
        cancelLabel={cancelLabel}
        loading={isSubmitting}
        disabled={isSubmitting}
      />
    </div>
  );
}
